import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export interface SpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Size of the spinner */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Color variant of the spinner */
  variant?: 'primary' | 'white' | 'muted';
  /** Optional label text below spinner */
  label?: string;
  /** Whether to center the spinner in its container */
  centered?: boolean;
  /** Screen reader text for accessibility */
  srText?: string;
}

const sizeClasses = {
  xs: 'w-3 h-3 border-[1.5px]',
  sm: 'w-4 h-4 border-2',
  md: 'w-6 h-6 border-2',
  lg: 'w-8 h-8 border-[2.5px]',
  xl: 'w-12 h-12 border-[3px]',
};

const variantClasses = {
  primary: 'border-border border-t-primary shadow-glow-subtle',
  white: 'border-white/20 border-t-white',
  muted: 'border-fg-muted/20 border-t-fg-muted',
};

export function Spinner({
  size = 'md',
  variant = 'primary',
  label,
  centered = false,
  srText,
  className,
  ...props
}: SpinnerProps) {
  const { t } = useTranslation();
  const resolvedSrText = srText ?? t('common:common.loading');
  const spinnerElement = (
    <div
      className={cn(
        'inline-flex flex-col items-center gap-2',
        centered && 'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
        className
      )}
      {...props}
    >
      <div
        className={cn(
          'rounded-full animate-spin',
          sizeClasses[size],
          variantClasses[variant],
          // Smooth animation with CSS
          'transition-transform duration-1000 ease-linear',
          // Respect reduced motion preference
          'motion-reduce:animate-none motion-reduce:border-t-4'
        )}
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <span className="sr-only">{resolvedSrText}</span>
      </div>
      {label && (
        <span
          className={cn(
            'text-fg-muted',
            size === 'xs' && 'text-xs',
            size === 'sm' && 'text-sm',
            size === 'md' && 'text-base',
            size === 'lg' && 'text-lg',
            size === 'xl' && 'text-xl'
          )}
        >
          {label}
        </span>
      )}
    </div>
  );

  if (centered) {
    return <div className="relative w-full h-full min-h-[100px]">{spinnerElement}</div>;
  }

  return spinnerElement;
}

// Compound components for common use cases
export function PageSpinner({ label }: { label?: string }) {
  const { t } = useTranslation();
  const resolvedLabel = label ?? t('common:common.loadingPage');

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-bg/50 backdrop-blur-sm z-modal">
      <Spinner size="lg" variant="primary" label={resolvedLabel} />
    </div>
  );
}

export const InlineSpinner = ({ label }: { label?: string }) => (
  <Spinner size="sm" variant="primary" label={label} />
);
